import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Cloud, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

/**
 * Manual "Back Up to Dropbox" (section 13). Select Content → this dialog
 * (Confirm Destination → Start Backup → View Status). There is no
 * existing endpoint that copies a B2 asset into Dropbox — the real
 * Dropbox integration in this codebase only (a) checks OAuth connection
 * health and (b) uploads already-rendered CLEANED exports, neither of
 * which is "back up this raw asset." So "Start Backup" never claims a
 * successful transfer — it honestly reports the Level 3 gap instead of
 * faking progress or success.
 */
export function DropboxBackupDialog({ open, onOpenChange, selection, dropboxConnected }) {
  const [started, setStarted] = useState(false);

  const handleClose = (v) => { if (!v) setStarted(false); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-md" data-testid="dropbox-backup-dialog">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Cloud className="h-4 w-4 text-[#3A7BFF]" />Back Up to Dropbox</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3">
            <p className="text-xs text-[#555E6E] mb-1">Selected content</p>
            <p className="text-sm text-white">{selection.length} item{selection.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg p-3">
            <div>
              <p className="text-xs text-[#555E6E] mb-1">Destination</p>
              <p className="text-sm text-white">Dropbox</p>
            </div>
            <Badge variant="outline" className={dropboxConnected ? 'border-green-500/30 text-green-400' : 'border-yellow-500/30 text-yellow-400'}>
              {dropboxConnected ? 'Connected' : 'Not connected'}
            </Badge>
          </div>
          {!dropboxConnected && (
            <Link to="/app/account/creator-profile" className="text-xs text-[#3A7BFF] hover:text-white flex items-center gap-1" data-testid="dropbox-backup-connect-link">
              Connect Dropbox in Creator Profile<ExternalLink className="h-3 w-3" />
            </Link>
          )}

          {!started ? (
            <p className="text-[11px] text-[#555E6E]">Your files stay safely in Backblaze B2 either way — this only copies them to Dropbox as an extra backup.</p>
          ) : (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="dropbox-backup-status">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200">Backup transfer isn't connected yet (Level 3) — no file was copied to Dropbox. Your originals remain safely in B2.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="border-white/10" onClick={() => handleClose(false)} data-testid="dropbox-backup-close-btn">Close</Button>
          {!started && <Button className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={() => setStarted(true)} data-testid="dropbox-backup-start-btn"><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Start Backup</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
